import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom, of } from "rxjs";
import { BehaviorSubject } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../environments/environment";

export interface RuntimeThemeColors {
  primary: string;
  secondary: string;
  palette?: Record<string, string>;
}

export interface RuntimeThemeBranding {
  accountIcon: string;
  favicon: string;
  organisationLogo: string;
}

interface RuntimeThemeConfig {
  theme: RuntimeThemeColors;
}

@Injectable({
  providedIn: "root",
})
export class ThemeRuntimeService {
  private readonly storageKey = "sv:runtime-theme:v1";
  private readonly brandingStorageKey = "sv:runtime-branding:v1";
  private readonly defaultTheme: RuntimeThemeColors = this.resolveDefaultThemeByCompany();
  private readonly defaultBranding: RuntimeThemeBranding = this.resolveDefaultBrandingByCompany();
  private activeTheme: RuntimeThemeColors = this.defaultTheme;
  private activeBranding: RuntimeThemeBranding = this.defaultBranding;
  private readonly brandingSubject = new BehaviorSubject<RuntimeThemeBranding>(this.defaultBranding);
  readonly branding$ = this.brandingSubject.asObservable();

  constructor(private readonly http: HttpClient) {}

  async initialize(): Promise<void> {
    const cachedTheme = this.getCachedTheme();
    if (cachedTheme) {
      this.applyTheme(cachedTheme, false);
    } else {
      const assetTheme = await this.getThemeFromAssets();
      if (assetTheme) {
        this.applyTheme(assetTheme, false);
      } else {
        this.applyTheme(this.defaultTheme, false);
      }
    }

    if (environment.enableRemoteThemeFetch) {
      const remoteTheme = await this.getThemeFromRemote();
      if (remoteTheme) {
        this.applyTheme(remoteTheme, true);
      }
    }

    const cachedBranding = this.getCachedBranding();
    if (cachedBranding) {
      this.applyBranding(cachedBranding, false);
    } else {
      this.applyBranding(this.defaultBranding, false);
    }
  }

  getActiveTheme(): RuntimeThemeColors {
    return { ...this.activeTheme };
  }

  getDefaultTheme(): RuntimeThemeColors {
    return { ...this.defaultTheme };
  }

  getActiveBranding(): RuntimeThemeBranding {
    return { ...this.activeBranding };
  }

  getDefaultBranding(): RuntimeThemeBranding {
    return { ...this.defaultBranding };
  }

  applyLoginResponseTheme(payload: any, persist = true): void {
    const organisation =
      payload?.organisation ??
      payload?.organization ??
      (this.hasOrganisationThemeKeys(payload) ? payload : null);

    this.applyTheme(
      {
        primary:
          this.normalizeHex(
            organisation?.organisationPrimaryColor ??
              organisation?.organizationPrimaryColor
          ) || this.defaultTheme.primary,
        secondary:
          this.normalizeHex(
            organisation?.organisationSecondaryColor ??
              organisation?.organizationSecondaryColor
          ) || this.defaultTheme.secondary,
      },
      persist
    );

    this.applyBranding(
      {
        accountIcon:
          this.normalizeAssetPath(
            organisation?.organisationIcon ?? organisation?.organizationIcon
          ) || this.defaultBranding.accountIcon,
        favicon:
          this.normalizeAssetPath(
            organisation?.organisationFavicon ??
              organisation?.organizationFavicon
          ) || this.defaultBranding.favicon,
        organisationLogo:
          this.normalizeAssetPath(
            organisation?.organisationLogo ?? organisation?.organizationLogo
          ) || this.defaultBranding.organisationLogo,
      },
      persist
    );
  }

  private hasOrganisationThemeKeys(payload: any): boolean {
    if (!payload || typeof payload !== "object") {
      return false;
    }

    return Boolean(
      payload.organisationPrimaryColor ||
        payload.organizationPrimaryColor ||
        payload.organisationSecondaryColor ||
        payload.organizationSecondaryColor ||
        payload.organisationIcon ||
        payload.organisationLogo ||
        payload.organizationIcon ||
        payload.organizationLogo ||
        payload.organisationFavicon ||
        payload.organizationFavicon
    );
  }

  applyTheme(theme: RuntimeThemeColors, persist = true): void {
    const normalized = this.normalizeTheme(theme);
    if (!normalized) {
      return;
    }

    this.activeTheme = normalized;
    const root = document.documentElement;
    const primaryRgb = this.hexToRgb(normalized.primary);
    const secondaryRgb = this.hexToRgb(normalized.secondary);

    root.style.setProperty("--sv-theme-primary", normalized.primary);
    root.style.setProperty("--sv-theme-secondary", normalized.secondary);
    root.style.setProperty("--bs-primary", normalized.primary);
    root.style.setProperty("--bs-secondary", normalized.secondary);

    if (primaryRgb) {
      root.style.setProperty("--bs-primary-rgb", `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    }

    if (secondaryRgb) {
      root.style.setProperty("--bs-secondary-rgb", `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
    }

    root.style.setProperty("--wallet-theme-primary", normalized.primary);
    root.style.setProperty("--wallet-theme-secondary", normalized.secondary);
    Object.entries(normalized.palette ?? {}).forEach(([name, value]) => {
      root.style.setProperty(`--sv-theme-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`, value);
    });

    if (persist) {
      this.persistTheme(normalized);
    }
  }

  clearThemeOverride(): void {
    localStorage.removeItem(this.getThemeStorageKey());
    this.applyTheme(this.defaultTheme, false);
  }

  applyBranding(branding: RuntimeThemeBranding, persist = true): void {
    const normalized = this.normalizeBranding(branding);
    if (!normalized) {
      return;
    }

    this.activeBranding = normalized;
    this.applyFavicon(normalized.favicon);
    this.brandingSubject.next({ ...normalized });

    if (persist) {
      this.persistBranding(normalized);
    }
  }

  clearBrandingOverride(): void {
    localStorage.removeItem(this.getBrandingStorageKey());
    this.applyBranding(this.defaultBranding, false);
  }

  private async getThemeFromAssets(): Promise<RuntimeThemeColors | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<RuntimeThemeConfig>("assets/theme-colors.json").pipe(
          map((res) => this.extractThemeFromPayload(res)),
          catchError(() => of(null))
        )
      );
      return response;
    } catch {
      return null;
    }
  }

  private async getThemeFromRemote(): Promise<RuntimeThemeColors | null> {
    const userId = localStorage.getItem("userID");
    if (!userId) {
      return null;
    }

    const endpoint = `${environment.apiUrl}users/theme_preferences/`;
    try {
      const response = await firstValueFrom(
        this.http.get<any>(endpoint, { withCredentials: true }).pipe(
          map((res) => this.extractThemeFromPayload(res)),
          catchError(() => of(null))
        )
      );
      return response;
    } catch {
      return null;
    }
  }

  private extractThemeFromPayload(payload: any): RuntimeThemeColors | null {
    const candidate =
      payload?.theme ??
      payload?.data?.theme ??
      payload?.data?.preferences?.theme ??
      (payload?.data?.primary && payload?.data?.secondary
        ? { primary: payload.data.primary, secondary: payload.data.secondary }
        : null);

    if (!candidate) {
      return null;
    }

    return this.normalizeTheme({
      primary: candidate.primary,
      secondary: candidate.secondary,
      palette: candidate.palette ?? {
        sky: candidate.sky,
        lightGray: candidate.lightGray,
        white: candidate.white,
        lime: candidate.lime,
        green: candidate.green,
      },
    });
  }

  private persistTheme(theme: RuntimeThemeColors): void {
    localStorage.setItem(this.getThemeStorageKey(), JSON.stringify(theme));
  }

  private persistBranding(branding: RuntimeThemeBranding): void {
    localStorage.setItem(this.getBrandingStorageKey(), JSON.stringify(branding));
  }

  private getCachedTheme(): RuntimeThemeColors | null {
    try {
      const raw = localStorage.getItem(this.getThemeStorageKey());
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return this.normalizeTheme(parsed);
    } catch {
      return null;
    }
  }

  private getCachedBranding(): RuntimeThemeBranding | null {
    try {
      const raw = localStorage.getItem(this.getBrandingStorageKey());
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return this.normalizeBranding(parsed);
    } catch {
      return null;
    }
  }

  private normalizeTheme(theme: RuntimeThemeColors | null | undefined): RuntimeThemeColors | null {
    if (!theme) {
      return null;
    }

    const primary = this.normalizeHex(theme.primary);
    const secondary = this.normalizeHex(theme.secondary);

    if (!primary || !secondary) {
      return null;
    }

    const eykonPalette = this.isEykon()
      ? { sky: "#b2cad4", lightGray: "#ebf1f6", white: "#ffffff", lime: "#acde2c", green: "#89c649" }
      : undefined;
    const palette = theme.palette
      ? Object.fromEntries(
          Object.entries(theme.palette)
            .map(([name, value]) => [name, this.normalizeHex(value)])
            .filter((entry): entry is [string, string] => Boolean(entry[1]))
        )
      : eykonPalette;

    return { primary, secondary, ...(palette ? { palette } : {}) };
  }

  private normalizeHex(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    const normalized = value.trim();
    const threeHex = /^#([0-9a-fA-F]{3})$/;
    const sixHex = /^#([0-9a-fA-F]{6})$/;

    if (sixHex.test(normalized)) {
      return normalized.toLowerCase();
    }

    const threeMatch = normalized.match(threeHex);
    if (!threeMatch) {
      return null;
    }

    const chars = threeMatch[1];
    return `#${chars[0]}${chars[0]}${chars[1]}${chars[1]}${chars[2]}${chars[2]}`.toLowerCase();
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const normalized = this.normalizeHex(hex);
    if (!normalized) {
      return null;
    }

    const clean = normalized.replace("#", "");
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return { r, g, b };
  }

  private normalizeBranding(branding: RuntimeThemeBranding | null | undefined): RuntimeThemeBranding | null {
    if (!branding) {
      return null;
    }

    const normalizedAccountIcon = this.normalizeAssetPath(branding.accountIcon);
    const accountIcon = normalizedAccountIcon === "assets/dashboard/sidebar/logo.svg"
      ? this.defaultBranding.accountIcon
      : normalizedAccountIcon || this.defaultBranding.accountIcon;
    const normalizedFavicon = this.normalizeAssetPath(branding.favicon);
    const favicon = this.isEykon()
      ? this.defaultBranding.favicon
      : normalizedFavicon || this.defaultBranding.favicon;
    const organisationLogo =
      this.normalizeAssetPath(branding.organisationLogo) || this.defaultBranding.organisationLogo;

    return {
      accountIcon,
      favicon,
      organisationLogo,
    };
  }

  private normalizeAssetPath(value: string | null | undefined): string | null {
    if (!value || typeof value !== "string") {
      return null;
    }

    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    if (
      normalized.startsWith("data:image/") ||
      normalized.startsWith("assets/") ||
      normalized.startsWith("/") ||
      normalized.startsWith("http://") ||
      normalized.startsWith("https://")
    ) {
      return normalized;
    }

    return null;
  }

  private applyFavicon(faviconUrl: string): void {
    if (faviconUrl === "favicon.png" || faviconUrl === "/favicon.png") {
      faviconUrl = "assets/branding/favicon.ico?v=5301";
    }
    const links = document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']");
    if (links.length === 1 && links[0].href === new URL(faviconUrl, document.baseURI).href) {
      return;
    }
    links.forEach(link => link.remove());

    const link = document.createElement("link");
    link.setAttribute("rel", "icon");
    link.setAttribute("href", faviconUrl);
    document.head.appendChild(link);
  }

  private resolveDefaultThemeByCompany(): RuntimeThemeColors {
    const company = String(environment.companyCheck || "").toLowerCase().trim();
    if (company === "eykon") {
      return {
        primary: "#00263d",
        secondary: "#326e71",
        palette: { sky: "#b2cad4", lightGray: "#ebf1f6", white: "#ffffff", lime: "#acde2c", green: "#89c649" },
      };
    }
    if (company === "landis") {
      return { primary: "#7ab800", secondary: "#007f7b" };
    }

    return { primary: "#0061f2", secondary: "#6900c7" };
  }

  private resolveDefaultBrandingByCompany(): RuntimeThemeBranding {
    const company = String(environment.companyCheck || "").toLowerCase().trim();
    if (company === "eykon") {
      return {
        accountIcon: "assets/eykon-assets/images/eykon-logo.png",
        favicon: "assets/eykon-assets/images/eykon-favicon.ico?v=20261015",
        organisationLogo: "assets/eykon-assets/images/eykon-logo.png",
      };
    }
    if (company === "landis") {
      return {
        accountIcon: "assets/landis-assets/images/sidebar_logo.png",
        favicon: "assets/landis-assets/images/favicon.png",
        organisationLogo: "assets/landis-assets/images/landis_logo.svg",
      };
    }

    return {
      accountIcon: "assets/branding/account-icon.png?v=5301",
      favicon: "assets/branding/favicon.ico?v=5301",
      organisationLogo: "assets/img/demo/logo.png",
    };
  }

  private isEykon(): boolean {
    return String(environment.companyCheck || "").toLowerCase().trim() === "eykon";
  }

  private getThemeStorageKey(): string {
    return this.isEykon() ? `${this.storageKey}:eykon` : this.storageKey;
  }

  private getBrandingStorageKey(): string {
    return this.isEykon() ? `${this.brandingStorageKey}:eykon` : this.brandingStorageKey;
  }
}
